import {
  Check,
  CheckCircle,
  ChevronLeft,
  Download,
  ImagePlus,
  Import,
} from "@tamagui/lucide-icons";
import * as DocumentPicker from "expo-document-picker";
import { observer } from "mobx-react-lite";
import Papa from "papaparse";
import React from "react";
import type { ModalProps } from "react-native";
import { Text, XStack, YStack } from "tamagui";
import { useStores } from "../../../../core/stores";
import { Body } from "../../../../ui/Body";
import { Button } from "../../../../ui/Button";
import { CenterContent } from "../../../../ui/CenterContent";
import { Group } from "../../../../ui/Group";
import { Label } from "../../../../ui/Label";
import { log } from "../../../../ui/log";
import { Modal } from "../../../../ui/Modal";
import { Subtext } from "../../../../ui/Subtext";
import { Title } from "../../../../ui/Title";
import { UserCircle } from "../../../../ui/UserCircle";

enum ImportStep {
  Upload = "upload",
  Preview = "preview",
  Done = "done",
}

type ImportedRow = {
  displayName: string;
  email: string;
  errors: string[];
};

export const BusinessPlanImportUsersModal = observer(
  ({
    businessPlanUid,
    visible,
    onClose,
    ...props
  }: {
    businessPlanUid: string;
    onClose: () => void;
  } & ModalProps) => {
    const { businessPlansStore } = useStores();

    const [step, setStep] = React.useState<ImportStep>(ImportStep.Upload);
    const [isLoading, setIsLoading] = React.useState(false);

    const [rows, setRows] = React.useState<ImportedRow[]>([]);

    const userCountWithErrors = React.useMemo(() => {
      return rows.filter((r) => r.errors.length > 0).length;
    }, [rows]);

    const totalImportCounter = rows.length - userCountWithErrors;

    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    const selectFile = React.useCallback(async () => {
      const result = await DocumentPicker.getDocumentAsync({
        type: "text/csv",
      });

      // Only web compatible?
      const file = result.output?.[0];

      if (!file) {
        log.dev("No file found");
        return;
      }

      setIsLoading(true);
      Papa.parse<ImportedRow>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const parsedRows: ImportedRow[] = [];
          const existingEmails = new Set(
            businessPlan?.allUsersSorted.map((m) => m.email.toLowerCase()),
          );

          results.data.forEach((row, _index: number) => {
            const name = (row.displayName || "").trim();
            const email = (row.email || "").trim().toLowerCase();
            const errors: string[] = [];

            if (!name) {
              errors.push("Missing name");
            }

            const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            if (!validEmail) {
              errors.push(`Invalid email "${email}"`);
            }

            if (existingEmails.has(email)) {
              errors.push("Email already in use");
            }

            parsedRows.push({ displayName: name, email, errors });
          });

          setRows(parsedRows);
          setStep(ImportStep.Preview);
          setIsLoading(false);
        },
        error: (_err) => {
          setIsLoading(false);
        },
      });
    }, [businessPlan]);

    if (!businessPlan) {
      return null;
    }

    const handleConfirmImport = async () => {
      setIsLoading(true);

      const batchSize = 99;

      // Filter out rows with errors first
      const validRows = rows.filter((r) => r.errors.length === 0);

      for (let i = 0; i < validRows.length; i += batchSize) {
        const batch = validRows.slice(i, i + batchSize);

        // Process the batch in parallel
        await Promise.all(
          batch.map((r) =>
            businessPlan.addUser({
              ...r,
              authedUsers: [],
              avatarUrl: "",
            }),
          ),
        );

        // If there's another batch, wait 1 second
        if (i + batchSize < validRows.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      setIsLoading(false);
      setStep(ImportStep.Done);
    };

    const downloadSampleCsv = () => {
      const sample =
        "displayName,email\nAlice Johnson,alice@example.com\nBob Smith,bob@example.com";

      const blob = new Blob([sample], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "sample_members.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <Modal
        modalId="business-import-users"
        onClose={onClose}
        visible={visible}
        {...props}
        header={
          <YStack gap="$space.medium">
            <XStack gap="$medium" alignItems="center">
              <Import />
              <Title>Import members</Title>
            </XStack>
          </YStack>
        }
        footer={
          <YStack gap="$space.medium" width={"100%"}>
            {step === ImportStep.Upload && (
              <YStack gap={"$medium"} width={"100%"}>
                <Button
                  type="secondary"
                  icon={Download}
                  onPress={downloadSampleCsv}
                >
                  Download sample
                </Button>
                <Subtext>
                  Upload a .csv file with <strong>name</strong> and{" "}
                  <strong>email</strong> columns.
                </Subtext>
                <Button
                  type="primary"
                  onPress={selectFile}
                  isLoading={isLoading}
                  icon={ImagePlus}
                  iconPosition={"before"}
                >
                  Upload CSV
                </Button>
              </YStack>
            )}

            {step === ImportStep.Preview && (
              <XStack
                gap="$medium"
                justifyContent="space-between"
                width={"100%"}
              >
                <Button
                  type="secondary"
                  icon={ChevronLeft}
                  onPress={() => setStep(ImportStep.Upload)}
                />
                <Button
                  icon={Check}
                  type="primary"
                  isLoading={isLoading}
                  onPress={handleConfirmImport}
                >
                  Confirm Import ({totalImportCounter})
                </Button>
              </XStack>
            )}

            {step === ImportStep.Done && (
              <Button
                type="primary"
                onPress={() => {
                  setStep(ImportStep.Upload);
                  setRows([]);
                  onClose();
                }}
              >
                Close
              </Button>
            )}
          </YStack>
        }
      >
        <YStack gap="$space.medium">
          {step === ImportStep.Upload && (
            <>
              <Label>Great, let's add more members to this event!</Label>
              <Body>
                Download the sample below, edit until ready and return to here
                when you are ready to continue.
              </Body>
            </>
          )}

          {step === ImportStep.Preview && (
            <>
              {!!userCountWithErrors && (
                <Text>
                  You have {userCountWithErrors} invalid entries so you will
                  currently import {rows.length - userCountWithErrors} members.
                </Text>
              )}
              <Text>Previewing {rows.length} new members:</Text>

              <Group borderBottom={false}>
                {rows.map((r, i) => (
                  <XStack
                    key={i}
                    gap={"$medium"}
                    alignItems="center"
                    width={"100%"}
                  >
                    <UserCircle name={r.displayName} />
                    <YStack gap={"$small"}>
                      <Text>{r.displayName}</Text>
                      <Text>{r.email}</Text>

                      {r.errors.length > 0 && (
                        <Text color="red">Errors: {r.errors.join(", ")}</Text>
                      )}
                    </YStack>
                  </XStack>
                ))}
              </Group>
            </>
          )}

          {step === ImportStep.Done && (
            <CenterContent
              icon={CheckCircle}
              title={"Import completed!"}
              subTitle={`${totalImportCounter} members added`}
            />
          )}
        </YStack>
      </Modal>
    );
  },
);
